import "./index.css";

import { ConfigProvider, Modal as AntdModal, type ModalProps as AntdModalProps, Row, theme } from "antd";
import type { FC, PropsWithChildren, PropsWithRef, ReactNode, Ref } from "react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTempStore } from "utils";

export interface ModalHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}
export interface ModalProps extends Omit<AntdModalProps, "open" | "footer"> {
  ref: Ref<ModalHandle>;
  footer?: ReactNode;
}
export const Modal: FC<PropsWithRef<PropsWithChildren<ModalProps>>> = forwardRef(
  (
    {
      children,
      footer,
      centered = true,
      maskClosable = false,
      destroyOnClose = false,
      onCancel,
      okText = "確定",
      ...props
    },
    ref
  ) => {
    const { token } = theme.useToken();
    const [isOpen, setIsOpen] = useState(false);
    const { setModalOpen, modalOpen } = useTempStore();
    const toggle = (value?: boolean) => {
      setIsOpen(value ?? !isOpen);
    };

    useEffect(() => {
      if (isOpen !== modalOpen) {
        setModalOpen(isOpen);
      }
    }, [isOpen]);

    useImperativeHandle(ref, () => {
      return {
        open: () => toggle(true),
        close: () => {
          toggle(false);
        },
        toggle,
      };
    }, []);

    return (
      <ConfigProvider
        theme={{
          components: {
            Button: { colorText: token.colorText },
            Modal: { contentBg: "white", boxShadow: "none", fontWeightStrong: 500, titleFontSize: 18 },
          },
        }}
      >
        <AntdModal
          centered={centered}
          className="!w-[30rem]"
          classNames={{ header: "!mb-6" }}
          destroyOnClose={destroyOnClose}
          maskClosable={maskClosable}
          okText={okText}
          onCancel={(e) => {
            onCancel ? onCancel(e) : toggle(false);
          }}
          open={isOpen}
          {...props}
          footer={footer}
        >
          <div className="rounded-sm bg-white">{isOpen && children}</div>
        </AntdModal>
      </ConfigProvider>
    );
  }
);

Modal.displayName = "Modal";
